import { useAtomValue } from 'jotai'
import React from 'react'
import { useFormContext } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

import { LabelingContainer } from '~/components/elements/LabelingContainer'
import { OrderedCustomerAttributeSelector } from '~/components/features/OrderedCustomerAttributeSelector'
import { useFormField } from '~/hooks/useFormField'
import { ensureExists, findByPath } from '~/utils'

import { linkCustomerAttributesInfoAtom } from '../../atoms'
import {
  type SurveyGroupSurvey,
  useMainQuerySurveys,
} from '../../hooks/useMainQuery'

import { type InputValues } from './utils'

export type AttachedCustomerAttributeSelectorProps = {
  index: number
  survey: SurveyGroupSurvey
}

export const AttachedCustomerAttributeSelector = ({
  index,
  survey,
}: AttachedCustomerAttributeSelectorProps) => {
  const { t } = useTranslation()
  const { control } = useFormContext<InputValues>()
  const field = useFormField(
    control,
    `customerAttributes.${index}.attachedCustomerAttributeMap.${survey.id}`,
  )

  const linkCustomerAttributesInfo = useAtomValue(
    linkCustomerAttributesInfoAtom,
  )
  const { customerAttributeGroup } = ensureExists(
    linkCustomerAttributesInfo,
    t('assertion_failed'),
  )
  const surveys = useMainQuerySurveys()
  const customerAttributes = React.useMemo(() => {
    const attachedCustomerAttributeGroupId =
      customerAttributeGroup.attachedCustomerAttributeGroupMap[survey.id]
    if (typeof attachedCustomerAttributeGroupId !== 'string') {
      return []
    }

    const survey_ = findByPath(surveys, 'id', survey.id)
    const customerAttributeGroup_ = findByPath(
      survey_?.customerAttributeGroups ?? [],
      'id',
      attachedCustomerAttributeGroupId,
    )

    return customerAttributeGroup_?.customerAttributes ?? []
  }, [
    customerAttributeGroup.attachedCustomerAttributeGroupMap,
    survey.id,
    surveys,
  ])

  return (
    <LabelingContainer label={survey.title} labelVariant="small">
      <OrderedCustomerAttributeSelector
        customerAttributes={customerAttributes}
        disabled={customerAttributes.length === 0}
        isNullSelectable={true}
        nullText={t('no_corresponding_customized_segments')}
        {...field}
      />
    </LabelingContainer>
  )
}
